/* eslint-disable @spaced-out/i18n/no-static-labels */
// @flow strict

import * as React from 'react';
import logger from 'src/utils/logger';
import {SimpleDropdown} from '@spaced-out/ui-design-system/lib/components/Dropdown';
import useI18n from 'src/hooks/useI18n';
import {isLanguageSupported} from 'src/utils/language-support';
// $FlowFixMe[untyped-import]
import {loadLanguageAndTranslationResourceBundle} from 'src/components/lib/i18n/new-i18n';

import css from './language-support.css';


export const getLanguageLabel = (key: string): string => {
  let label = 'English';
  switch (key) {
    case 'en':
      label = 'English';
      break;
    case 'fr-CA':
      label = 'Français canadien';
      break;
    case 'es-419':
      label = 'Español latinoamericano';
      break;
    case 'it':
      label = 'Italiano';
      break;
    case 'fr':
      label = 'Français';
      break;
    case 'es':
      label = 'Español';
      break;
    case 'de':
      label = 'Deutsch';
      break;
    case 'hu':
      label = 'Magyar';
      break;
    case 'ja':
      label = '日本語';
      break;
    case 'nl':
      label = 'Nederlands';
      break;
    case 'pt':
      label = 'Português';
      break;
    case 'sv':
      label = 'Svenska';
      break;
  }
  return label;
};

export const LanguageSupport = ({
  language,
  persistLanguage = true,
}: {
  language?: string,
  persistLanguage?: boolean,
}): React.Node => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const initializedLanguage = labelI18nInstance.language;

  let languageFromLocalStorage = '';
  try {
    languageFromLocalStorage = localStorage.getItem('selectedLanguage');
  } catch (error) {
    logger.error('Error detecting localStorage language:', error);
  }

  const initialLanguage =
    language || initializedLanguage || languageFromLocalStorage || 'en';
  const [selectedLanguage, setSelectedLanguage] = React.useState({
    key: initialLanguage,
    label: getLanguageLabel(initialLanguage),
  });

  const onLanguageSelect = (option) => {
    labelI18nInstance
      .changeLanguage(option.key)
      .then(() => {
        if (persistLanguage) {
          localStorage.setItem('selectedLanguage', option.key);
        }
        setSelectedLanguage(option);
      })
      .catch((err) => {
        console.error('Error changing language:', err);
      });
  };

  // Effect to handle dynamic language changes from external sources (e.g., API)
  // This ensures the app renders with the new language and updates the dropdown selection
  // Currently used in the scheduler app where user language is fetched from API and we need to render the app with the new language
  React.useEffect(() => {
    const loadLanguage = async () => {
      // Check if language is supported and different from current selection
      if (
        language &&
        isLanguageSupported(language) &&
        language !== selectedLanguage.key
      ) {
        // create language object with key and localized label
        const newLanguage = {
          key: language,
          label: getLanguageLabel(language),
        };

        // load translation resources for the new language
        await loadLanguageAndTranslationResourceBundle(language);

        // update the selected language in dropdown and i18n instance
        onLanguageSelect(newLanguage);
      }
    };

    loadLanguage();
  }, [language]);

  return (
    <div className={css.container}>
      <SimpleDropdown
        onChange={onLanguageSelect}
        iconLeftName="language"
        iconLeftType="solid"
        placeholder={t('Select_language', 'Select language')}
        value={selectedLanguage.label}
        selectedKeys={[initialLanguage]}
        options={[
          {label: 'English', key: 'en'},
          {label: 'Français canadien', key: 'fr-CA'},
          {label: 'Español latinoamericano', key: 'es-419'},
          {label: 'Italiano', key: 'it'},
          {label: 'Français', key: 'fr'},
          {label: 'Español', key: 'es'},
          {label: 'Deutsch', key: 'de'},
          {label: 'Magyar', key: 'hu'},
          {label: '日本語', key: 'ja'},
          {label: 'Nederlands', key: 'nl'},
          {label: 'Português', key: 'pt'},
          {label: 'Svenska', key: 'sv'},
        ]}
        size="small"
        selectedKeys={[selectedLanguage.key ?? '']}
        classNames={{wrapper: css.dropdown}}
      />
    </div>
  );
};
